import React, { useState } from 'react';
import {
  CcfHeader,
  CcfSettingsIcon,
  CcfTabs,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  CcfAgentSkills,
  CcfAppHealth,
  CcfAVNotifications,
  CcfDisplayAndAccessability,
  CcfLoginVoicePreferanceTab,
  getApplicationDirection,
} from '@nice-ccf/ui-components';
import {
  Box,
  Direction,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useSelector } from 'react-redux';
import ccfAppSettingsStyles from './ccf-app-settings.styles';

export enum SettingsDropdownValues {
  AV_NOTIFICATIONS = 'A/V Notifications',
  DISPLAY = 'Display & Accessibility',
  VOICE = 'Login & Voice Preferences',
  INFORMATION = 'Information',
  AGENT_SKILLS = 'Agent Skills',
}

interface SettingsTab {
  element: React.ReactElement;
  label: string;
  isActive: boolean;
}

/* eslint-disable-next-line */
export interface CcfAppSettingsProps {}

/**
 * used to show App Settings static screen
 * @param props -CcfAppSettingsProps
 * @example `<CcfAppSettings inboxCardState={inboxCardState} />`
 */
export function CcfAppSettings(props: CcfAppSettingsProps) {
  const theme = useTheme();
  const appDirection: Direction = useSelector(getApplicationDirection);
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const [tab, setTab] = React.useState(0);
  const [translate] = useTranslator();
  const styles = ccfAppSettingsStyles(theme);
  const [settingsValue, setSettingsValue] = useState(
    SettingsDropdownValues.VOICE
  );
  const settingsTabs: SettingsTab[] = [
    {
      element: <CcfLoginVoicePreferanceTab />,
      isActive: true,
      label: translate('loginAndVoicePreference'),
    },
    {
      element: <CcfAVNotifications />,
      isActive: true,
      label: translate('avNotifications'),
    },
    {
      element: <CcfDisplayAndAccessability />,
      isActive: true,
      label: translate('displayAndAccessability'),
    },
    {
      element: <CcfAppHealth />,
      isActive: true,
      label: translate('information'),
    },
    {
      element: <CcfAgentSkills />,
      isActive: true,
      label: translate('agentSkills'),
    },
  ];
  const activeTabs = settingsTabs.filter((tab) => tab.isActive);

  /**
   *
   * @param e - event
   * @example handleChange
   */
  const handleChange = (event: SelectChangeEvent) => {
    setSettingsValue(event.target.value as SettingsDropdownValues);
  };

  /**
   *
   * @param e - event
   * @example handleTabChange
   */
  const handleTabChange = (newValue: number) => {
    setTab(newValue);
  };

  return (
    <Box sx={styles.FullViewSettingsCard}>
      {isSmView ? (
        <>
          <Box sx={styles.settingsHeader}>
            <CcfHeader
              headerText={translate('settings')}
              RightIcon
              showDragIcon
              direction={appDirection}
            />
          </Box>
          <Paper square elevation={0} sx={styles.settingsContainer}>
            <div style={styles.menuTab}>
              <Select
                labelId="settings-select-label"
                id="settings-select"
                value={settingsValue}
                onChange={handleChange}
                sx={{ width: { xs: '100%', lg: '255px' }, height: '40px' }}
              >
                <MenuItem value={SettingsDropdownValues.VOICE}>
                  {translate('loginAndVoicePreference')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.AV_NOTIFICATIONS}>
                  {translate('avNotifications')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.DISPLAY}>
                  {translate('displayAndAccessability')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.INFORMATION}>
                  {translate('information')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.AGENT_SKILLS}>
                  {translate('agentSkills')}
                </MenuItem>
              </Select>
            </div>
            {settingsValue === SettingsDropdownValues.VOICE && (
              <CcfLoginVoicePreferanceTab />
            )}
            {settingsValue === SettingsDropdownValues.AV_NOTIFICATIONS && (
              <CcfAVNotifications />
            )}
            {settingsValue === SettingsDropdownValues.DISPLAY && (
              <CcfDisplayAndAccessability />
            )}
            {settingsValue === SettingsDropdownValues.INFORMATION && (
              <CcfAppHealth />
            )}
            {settingsValue === SettingsDropdownValues.AGENT_SKILLS && (
              <CcfAgentSkills />
            )}
          </Paper>
        </>
      ) : (
        <>
          <CcfHeader
            LeftIcon={
              <CcfSettingsIcon
                color="primary"
                viewBox="-2 -2 23 23"
                fontSize="small"
              />
            }
            headerText={translate('settings')}
            RightIcon
            direction={appDirection}
          />
          <CcfTabs
            currentTab={tab}
            variant={CcfTabs.Variant.FULL_WIDTH}
            setCurrentTab={handleTabChange}
            bottomBorder={true}
          >
            {activeTabs.map((tab, index) => (
              <CcfTabs.TabPanel key={index} label={tab.label}>
                <Box sx={styles.container}>{tab.element}</Box>
              </CcfTabs.TabPanel>
            ))}
          </CcfTabs>
        </>
      )}
    </Box>
  );
}

export default CcfAppSettings;
